'use client'

import { Banners } from '@/components/banners'
import { CreatorAvatars } from '@/components/creator-avatars'
import Image from '@/components/image'
import Show from '@/components/show'
// import { UpperThumbnails } from '@/components/upper-thumbnails'
import { VideoThumbnails } from '@/components/video-thumbnails'
import { siteConfig } from '@/config/site'
import { ChannelType } from '@/constants/channel'
import { VideoType, getSectionTitle } from '@/constants/video'
// import { useChannelIds } from '@/lib/api/hooks/use-channel'
import { GetUser } from '@/lib/api/hooks/use-user'
import {
  useEventVideos,
  useFeaturedVideos,
  useGuessYouLikeVideos,
  useNewVideos,
  usePopularVideos,
  useStarVideos,
} from '@/lib/api/hooks/use-videos'
import { getIdsBySexualCategory } from '@/lib/utils'
import { useGuestStore } from '@/store'
import { useQuery } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useCallback, useMemo } from 'react'

export function useUser() {
  return useQuery({
    queryKey: ['user'],
    queryFn: () => GetUser(),
  })
}

export function Home() {
  const t = useTranslations()
  // @ts-ignore
  const { data: user } = useUser() as { data: User }
  const { selectedTags } = useGuestStore()
  const sexualCategoryIds = !!user ? user?.categoryIds : selectedTags

  // event
  const {
    data: eventVideosData,
    fetchNextPage: fetchNextEventVideosPage,
    hasNextPage: hasNextEventVideosPage,
    isFetchingNextPage: isFetchingNextEventVideosPage,
    isLoading: isLoadingEventVideos,
  } = useEventVideos({
    params: {
      pageSize: siteConfig.pagination.pageSize,
      userId: user?.id,
      sexualCategoryIds,
      categoryIds: siteConfig.homePage.event.categoryIds,
      // videoIds, 全站
    },
  })

  const eventVideos = useMemo(() => {
    const list = eventVideosData?.pages?.flatMap(page => page?.list ?? []) ?? []
    const pagination = eventVideosData?.pages?.flatMap(
      page => page?.pagination,
    )[0]
    return {
      list,
      pagination,
    }
  }, [eventVideosData?.pages])

  const handleLoadMoreEventVideosClick = useCallback(() => {
    if (hasNextEventVideosPage && !isFetchingNextEventVideosPage) {
      fetchNextEventVideosPage()
    }
  }, [
    fetchNextEventVideosPage,
    hasNextEventVideosPage,
    isFetchingNextEventVideosPage,
  ])

  const {
    data: starVideosData,
    fetchNextPage: fetchNextStarVideosPage,
    hasNextPage: hasNextStarVideosPage,
    isFetchingNextPage: isFetchingNextStarVideosPage,
    isLoading: isLoadingStarVideos,
  } = useStarVideos({
    params: {
      pageSize: siteConfig.pagination.pageSize,
      userId: user?.id,
      sexualCategoryIds,
      videoIds: getIdsBySexualCategory(
        sexualCategoryIds,
        siteConfig.homePage.star.videoIds,
      ),
    },
  })

  const starVideos = useMemo(() => {
    const list = starVideosData?.pages?.flatMap(page => page?.list ?? []) ?? []
    const pagination = starVideosData?.pages?.flatMap(
      page => page?.pagination,
    )[0]

    return {
      list,
      pagination,
    }
  }, [starVideosData?.pages])

  const handleLoadMoreStarVideosClick = useCallback(() => {
    if (hasNextStarVideosPage && !isFetchingNextStarVideosPage) {
      fetchNextStarVideosPage()
    }
  }, [
    fetchNextStarVideosPage,
    hasNextStarVideosPage,
    isFetchingNextStarVideosPage,
  ])

  const {
    data: featuredVideosData,
    fetchNextPage: fetchNextFeaturedVideosPage,
    hasNextPage: hasNextFeaturedVideosPage,
    isFetchingNextPage: isFetchingNextFeaturedVideosPage,
    isLoading: isLoadingFeaturedVideos,
  } = useFeaturedVideos({
    params: {
      pageSize: siteConfig.pagination.pageSize,
      userId: user?.id,
      channelIds: siteConfig.homePage.featured.channelIds,
      sexualCategoryIds,
    },
  })

  const {
    data: newVideosData,
    fetchNextPage: fetchNextNewPage,
    hasNextPage: hasNextNewPage,
    isFetchingNextPage: isFetchingNextNewPage,
    isLoading: isLoadingNewVideos,
  } = useNewVideos({
    params: {
      pageSize: siteConfig.pagination.pageSize,
      userId: user?.id,
      sexualCategoryIds,
    },
  })

  const {
    data: popularVideosData,
    fetchNextPage: fetchNextPopularPage,
    hasNextPage: hasNextPopularPage,
    isFetchingNextPage: isFetchingNextPopularPage,
    isLoading: isLoadingPopularVideos,
  } = usePopularVideos({
    params: {
      pageSize: siteConfig.pagination.pageSize,
      userId: user?.id,
      categoryIds: !!user ? user?.categoryIds : selectedTags,
    },
  })

  const {
    data: guessYouLikeVideosData,
    fetchNextPage: fetchNextGuessYouLikePage,
    hasNextPage: hasNextGuessYouLikePage,
    isFetchingNextPage: isFetchingNextGuessYouLikePage,
    isLoading: isLoadingGuessYouLikeVideos,
  } = useGuessYouLikeVideos({
    params: {
      // CAVEAT: 前端幫忙過濾標籤會濾掉太多影片，所以加開域閥值
      pageSize: siteConfig.pagination.biggerPageSize,
      userId: user?.id,
      sexualCategoryIds,
    },
  })

  const handleLoadMoreFeaturedVideosClick = useCallback(() => {
    if (hasNextFeaturedVideosPage && !isFetchingNextFeaturedVideosPage) {
      fetchNextFeaturedVideosPage()
    }
  }, [
    fetchNextFeaturedVideosPage,
    hasNextFeaturedVideosPage,
    isFetchingNextFeaturedVideosPage,
  ])

  const handleLoadNewMoreClick = useCallback(() => {
    if (hasNextNewPage && !isFetchingNextNewPage) {
      fetchNextNewPage()
    }
  }, [fetchNextNewPage, hasNextNewPage, isFetchingNextNewPage])

  const handleLoadPopularMoreClick = useCallback(() => {
    if (hasNextPopularPage && !isFetchingNextPopularPage) {
      fetchNextPopularPage()
    }
  }, [fetchNextPopularPage, hasNextPopularPage, isFetchingNextPopularPage])

  const handleLoadGuessYouLikeMoreClick = useCallback(() => {
    if (hasNextGuessYouLikePage && !isFetchingNextGuessYouLikePage) {
      fetchNextGuessYouLikePage()
    }
  }, [
    fetchNextGuessYouLikePage,
    hasNextGuessYouLikePage,
    isFetchingNextGuessYouLikePage,
  ])

  const featuredVideos = useMemo(() => {
    const list =
      featuredVideosData?.pages?.flatMap(page => page?.list ?? []) ?? []
    const pagination = featuredVideosData?.pages?.flatMap(
      page => page?.pagination,
    )[0]

    return {
      list,
      pagination,
    }
  }, [featuredVideosData?.pages])

  const allNewVideos = useMemo(() => {
    const list = newVideosData?.pages?.flatMap(page => page?.list ?? []) ?? []
    const pagination = newVideosData?.pages?.flatMap(
      page => page?.pagination,
    )[0]

    return {
      list,
      pagination,
    }
  }, [newVideosData?.pages])

  const allPopularVideos = useMemo(() => {
    const list =
      popularVideosData?.pages?.flatMap(page => page?.list ?? []) ?? []
    const pagination = popularVideosData?.pages?.flatMap(
      page => page?.pagination,
    )[0]

    return {
      list,
      pagination,
    }
  }, [popularVideosData?.pages])

  const allGuessYouLikeVideos = useMemo(() => {
    const allVideos =
      guessYouLikeVideosData?.pages?.flatMap(page => page?.list ?? []) ?? []
    const pagination = guessYouLikeVideosData?.pages?.flatMap(
      page => page?.pagination,
    )[0]

    // NOTE: 加入訪客判斷
    const categoryIds = !!user ? user?.categoryIds : selectedTags
    const mySexualTags =
      categoryIds?.filter((id: number) =>
        siteConfig.sexualPreferenceCategoryIds.includes(id),
      ) || []

    const filteredList = allVideos.filter(video =>
      mySexualTags.includes(video.sexualCategory?.id),
    )

    return {
      list: filteredList,
      pagination,
    }
  }, [guessYouLikeVideosData?.pages, selectedTags, user])

  const sortingPivots = getIdsBySexualCategory(
    sexualCategoryIds,
    siteConfig.homePage.star.videoIds,
  )

  // NOTE: 前端自行按照指定順序進行排序
  const sortedStarVideos = starVideos?.list?.sort((a, b) => {
    const aIndex = sortingPivots.indexOf(parseInt(a.id, 10))
    const bIndex = sortingPivots.indexOf(parseInt(b.id, 10))
    if (aIndex === -1 && bIndex === -1) return 0 // Both not in sortingPivots
    if (aIndex === -1) return 1 // a not in sortingPivots, b comes first
    if (bIndex === -1) return -1 // b not in sortingPivots, a comes first
    return aIndex - bIndex // Both in sortingPivots, sort by index
  })

  return (
    <div className='mt-0 space-y-5 pb-5 lg:space-y-10 lg:pb-10'>
      <Banners />
      <div className='px-3 lg:px-0'>
        {/* NOTE: 0507 MFANS STAR */}
        <Show when={siteConfig.homePage.star.show}>
          <div className='mb-5 lg:mb-[60px]'>
            <div className='mb-3 flex items-center justify-between lg:mb-5 lg:px-6'>
              <div className='flex h-10 items-center gap-x-2 lg:h-[48px]'>
                <div className='h-6 w-7'>
                  <Image alt='MFans' src='/mfans.png' width={56} height={49} />
                </div>
                <div className='flex'>
                  <span className='text-xl font-black leading-5 text-white'>
                    {t(getSectionTitle(VideoType.Star))}
                  </span>
                </div>
              </div>
              <Link
                href={`/series/star`}
                className='flex cursor-pointer items-center gap-x-1 text-sm font-bold text-[#808592]'
              >
                {t('page.home.action.viewMore')}
                <Image src='/arrow-right.png' width={20} height={20} alt='' />
              </Link>
            </div>
            <VideoThumbnails
              className='flex items-center gap-x-3'
              items={sortedStarVideos}
              pagination={starVideos?.pagination}
              isLoading={isLoadingStarVideos}
              hasNextPage={hasNextStarVideosPage}
              isFetchingNextPage={isFetchingNextStarVideosPage}
              handleLoadMoreClick={handleLoadMoreStarVideosClick}
            />
          </div>
        </Show>
        {/* NOTE: 当季事件 */}
        <Show when={siteConfig.homePage.event.show}>
          <div className='mb-5 lg:mb-[60px]'>
            <div className='mb-3 flex items-center justify-between lg:mb-5 lg:px-6'>
              <div className='flex h-10 items-center gap-x-2 lg:h-[48px]'>
                <div className='h-6 w-7'>
                  <Image alt='MFans' src='/mfans.png' width={56} height={49} />
                </div>
                <div className='flex'>
                  <span className='text-xl font-black leading-5 text-white'>
                    {t(getSectionTitle(VideoType.Event))}
                  </span>
                </div>
              </div>
              <Link
                href={`/series/${VideoType.Event}`}
                className='flex cursor-pointer items-center gap-x-1 text-sm font-bold text-[#808592]'
              >
                {t('page.home.action.viewMore')}
                <Image src='/arrow-right.png' width={20} height={20} alt='' />
              </Link>
            </div>
            <VideoThumbnails
              className='flex items-center gap-x-3'
              items={eventVideos?.list}
              pagination={eventVideos?.pagination}
              isLoading={isLoadingEventVideos}
              hasNextPage={hasNextEventVideosPage}
              isFetchingNextPage={isFetchingNextEventVideosPage}
              handleLoadMoreClick={handleLoadMoreEventVideosClick}
            />
          </div>
        </Show>
        {/* NOTE: MFANS 精選 */}
        <Show when={siteConfig.homePage.featured.show}>
          <div className='mb-5 lg:mb-[60px]'>
            <div className='mb-3 flex items-center justify-between lg:mb-5 lg:px-6'>
              <div className='flex h-10 items-center gap-x-2 lg:h-[48px]'>
                <div className='h-6 w-7'>
                  <Image alt='MFans' src='/mfans.png' width={56} height={49} />
                </div>
                <div className='flex'>
                  <span className='text-xl font-black leading-5 text-white'>
                    {t(getSectionTitle(VideoType.Featured))}
                  </span>
                </div>
              </div>
              <Link
                href={`/series/${VideoType.Featured}`}
                className='flex cursor-pointer items-center gap-x-1 text-sm font-bold text-[#808592]'
              >
                {t('page.home.action.viewMore')}
                <Image src='/arrow-right.png' width={20} height={20} alt='' />
              </Link>
            </div>
            <VideoThumbnails
              className='flex items-center gap-x-3'
              items={featuredVideos?.list}
              pagination={featuredVideos?.pagination}
              isLoading={isLoadingFeaturedVideos}
              hasNextPage={hasNextFeaturedVideosPage}
              isFetchingNextPage={isFetchingNextFeaturedVideosPage}
              handleLoadMoreClick={handleLoadMoreFeaturedVideosClick}
            />
          </div>
        </Show>
        {/* NOTE: 最新上傳影片 */}
        <div className='mb-5 lg:mb-[60px]'>
          <div className='mb-3 flex items-center justify-between lg:mb-5 lg:px-6'>
            <div className='flex h-10 items-center gap-x-2 lg:h-[48px]'>
              <div className='h-6 w-7'>
                <Image alt='MFans' src='/mfans.png' width={56} height={49} />
              </div>
              <div className='flex'>
                <span className='text-xl font-black leading-5 text-white'>
                  {t(getSectionTitle(VideoType.New))}
                </span>
              </div>
            </div>
            <Link
              href={`/series/${VideoType.New}`}
              className='flex cursor-pointer items-center gap-x-1 text-sm font-bold text-[#808592]'
            >
              {t('page.home.action.viewMore')}
              <Image src='/arrow-right.png' width={20} height={20} alt='' />
            </Link>
          </div>
          <VideoThumbnails
            className='flex items-center gap-x-3'
            items={allNewVideos?.list}
            pagination={allNewVideos?.pagination}
            isLoading={isLoadingNewVideos}
            handleLoadMoreClick={handleLoadNewMoreClick}
            hasNextPage={hasNextNewPage}
            isFetchingNextPage={isFetchingNextNewPage}
          />
        </div>
        {/* NOTE: 熱門影片 */}
        <div className='mb-5 lg:mb-[60px]'>
          <div className='mb-3 flex items-center justify-between lg:mb-5 lg:px-6'>
            <div className='flex h-10 items-center gap-x-2 lg:h-[48px]'>
              <div className='h-6 w-7'>
                <Image alt='MFans' src='/mfans.png' width={56} height={49} />
              </div>
              <div className='flex'>
                <span className='text-xl font-black leading-5 text-white'>
                  {t(getSectionTitle(VideoType.Popular))}
                </span>
              </div>
            </div>
            <Link
              href={`/series/${VideoType.Popular}`}
              className='flex cursor-pointer items-center gap-x-1 text-sm font-bold text-[#808592]'
            >
              {t('page.home.action.viewMore')}
              <Image src='/arrow-right.png' width={20} height={20} alt='' />
            </Link>
          </div>
          <VideoThumbnails
            className='flex items-center gap-x-3'
            items={allPopularVideos?.list}
            pagination={allPopularVideos?.pagination}
            isLoading={isLoadingPopularVideos}
            handleLoadMoreClick={handleLoadPopularMoreClick}
            hasNextPage={hasNextPopularPage}
            isFetchingNextPage={isFetchingNextPopularPage}
          />
        </div>
        {/* NOTE: 創作者專區 */}
        <div className='mb-5 lg:mb-[60px]'>
          <div className='mb-3 flex items-center justify-between lg:mb-5 lg:px-6'>
            <div className='flex h-10 items-center gap-x-2 lg:h-[48px]'>
              <div className='h-6 w-7'>
                <Image alt='MFans' src='/mfans.png' width={56} height={49} />
              </div>
              <div className='flex'>
                <span className='text-xl font-black leading-5 text-white'>
                  {t(getSectionTitle(VideoType.TopTen))}
                </span>
              </div>
            </div>
            {/* <span className='flex cursor-pointer items-center gap-x-1 text-sm font-bold text-[#808592]'>
                {t('page.home.action.viewMore')}
                <Image src='/arrow-right.png' width={20} height={20} alt='' />
              </span>
            */}
          </div>
          <CreatorAvatars
            type={ChannelType.Recommendation}
            pageSize={siteConfig.pagination.pageSize}
          />
        </div>
        {/* NOTE: 猜你喜歡這些作品 */}
        <div className='mb-5 lg:mb-0'>
          <div className='mb-3 flex items-center justify-between lg:mb-5 lg:px-6'>
            <div className='flex h-10 items-center gap-x-2 lg:h-[48px]'>
              <div className='h-6 w-7'>
                <Image alt='MFans' src='/mfans.png' width={56} height={49} />
              </div>
              <div className='flex'>
                <span className='text-xl font-black leading-5 text-white'>
                  {t(getSectionTitle(VideoType.GuessYouLike))}
                </span>
              </div>
            </div>
            <Link
              href={`/series/${VideoType.GuessYouLike}`}
              className='flex cursor-pointer items-center gap-x-1 text-sm font-bold text-[#808592]'
            >
              {t('page.home.action.viewMore')}
              <Image src='/arrow-right.png' width={20} height={20} alt='' />
            </Link>
          </div>
          <VideoThumbnails
            className='flex items-center gap-x-3'
            items={allGuessYouLikeVideos?.list}
            pagination={allGuessYouLikeVideos?.pagination}
            isLoading={isLoadingGuessYouLikeVideos}
            handleLoadMoreClick={handleLoadGuessYouLikeMoreClick}
            hasNextPage={hasNextGuessYouLikePage}
            isFetchingNextPage={isFetchingNextGuessYouLikePage}
          />
        </div>
      </div>
    </div>
  )
}
